import { create } from 'zustand';
import type { AiSource, AiSuggestion, AiRating, AiChatSummary } from '../api/ai';

export interface ChatMsg {
  key: string;
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: AiSource[] | null;
  suggestions?: AiSuggestion[] | null;
  rating?: AiRating | null;
  streaming?: boolean;
}

export type AiStatus = 'idle' | 'thinking' | 'streaming';
export type AiLoad = 'normal' | 'busy' | 'overloaded';
export type AiContextLevel = 'ok' | 'warn';

interface AiChatStore {
  chatId: string | null;
  messages: ChatMsg[];
  status: AiStatus;
  load: AiLoad;
  context: AiContextLevel;
  error: string | null;

  chats: AiChatSummary[];
  loadingChats: boolean;

  setChatId: (id: string | null) => void;
  setStatus: (s: AiStatus) => void;
  setLoad: (l: AiLoad) => void;
  setContext: (c: AiContextLevel) => void;
  setError: (e: string | null) => void;
  setMessages: (msgs: ChatMsg[]) => void;
  setChats: (chats: AiChatSummary[]) => void;
  setLoadingChats: (v: boolean) => void;
  loadChat: (chatId: string, msgs: ChatMsg[]) => void;

  addUserMessage: (content: string) => void;
  startAssistant: () => void;
  appendToken: (delta: string) => void;
  finalizeAssistant: (final: {
    messageId?: string;
    content?: string;
    sources?: AiSource[] | null;
    suggestions?: AiSuggestion[] | null;
  }) => void;
  abortStream: (error: string) => void;
  setRating: (messageId: string, rating: AiRating | null) => void;
  reset: () => void;
}

export function isCurrentChatEvent(currentChatId: string | null, eventChatId?: string): boolean {
  return !eventChatId || eventChatId === currentChatId;
}

let seq = 0;
const nextKey = () => `m${Date.now()}_${seq++}`;

export const useAiChatStore = create<AiChatStore>((set) => ({
  chatId: null,
  messages: [],
  status: 'idle',
  load: 'normal',
  context: 'ok',
  error: null,
  chats: [],
  loadingChats: false,

  setChatId: (chatId) => set({ chatId }),
  setStatus: (status) => set({ status }),
  setLoad: (load) => set({ load }),
  setContext: (context) => set({ context }),
  setError: (error) => set({ error }),
  setMessages: (messages) => set({ messages }),
  setChats: (chats) => set({ chats }),
  setLoadingChats: (loadingChats) => set({ loadingChats }),
  loadChat: (chatId, messages) => set({ chatId, messages, status: 'idle', context: 'ok', error: null }),

  addUserMessage: (content) =>
    set((s) => ({
      messages: [...s.messages, { key: nextKey(), role: 'user', content }],
      status: 'thinking',
      error: null,
    })),

  startAssistant: () =>
    set((s) => {
      const last = s.messages[s.messages.length - 1];
      if (last?.role === 'assistant' && last.streaming) return s;
      return {
        messages: [...s.messages, { key: nextKey(), role: 'assistant', content: '', streaming: true }],
        status: 'streaming',
      };
    }),

  appendToken: (delta) =>
    set((s) => {
      const msgs = s.messages.slice();
      let last = msgs[msgs.length - 1];
      if (!last || last.role !== 'assistant' || !last.streaming) {
        last = { key: nextKey(), role: 'assistant', content: '', streaming: true };
        msgs.push(last);
      }
      msgs[msgs.length - 1] = { ...last, content: last.content + delta };
      return { messages: msgs, status: 'streaming' };
    }),

  finalizeAssistant: (final) =>
    set((s) => {
      const msgs = s.messages.slice();
      const last = msgs[msgs.length - 1];
      if (last?.role === 'assistant' && last.streaming) {
        msgs[msgs.length - 1] = {
          ...last,
          id: final.messageId ?? last.id,
          content: final.content ?? last.content,
          sources: final.sources ?? null,
          suggestions: final.suggestions ?? null,
          streaming: false,
        };
        return { messages: msgs, status: 'idle' };
      }
      if (final.content) {
        msgs.push({
          key: nextKey(),
          id: final.messageId,
          role: 'assistant',
          content: final.content,
          sources: final.sources ?? null,
          suggestions: final.suggestions ?? null,
          streaming: false,
        });
        return { messages: msgs, status: 'idle' };
      }
      return { status: 'idle' };
    }),

  abortStream: (error) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.streaming ? { ...m, streaming: false } : m)),
      status: 'idle',
      error,
    })),

  setRating: (messageId, rating) =>
    set((s) => ({
      messages: s.messages.map((m) => (m.id === messageId ? { ...m, rating } : m)),
    })),

  reset: () => set({ chatId: null, messages: [], status: 'idle', context: 'ok', error: null }),
}));
